import type { ActionNature, ActionState, ActionVerdict } from '@seoplum/core'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AiRunDetail } from './admin-ai'
import { api, unwrap } from './api'

/**
 * The deployment's own view of the actions feed.
 *
 * Nothing here is tenant-scoped, which is the point: a detector is a claim we
 * make about every site it runs against, so whether it is worth shipping is a
 * question only a cross-tenant read can answer. Every one of these is behind
 * the staff gate and answers 404 to everyone else.
 */

/** The six ways the list is worth cutting. `suppression` is the one a customer
 * has no equivalent of — the rows the model vetoed never reach their list. */
export const ACTION_DIMENSIONS = [
  'type',
  'state',
  'nature',
  'organization',
  'site',
  'suppression',
] as const

export type ActionDimension = (typeof ACTION_DIMENSIONS)[number]

export const ACTION_DIMENSION_LABELS: Record<ActionDimension, string> = {
  type: 'Check',
  state: 'State',
  nature: 'Nature',
  organization: 'Workspace',
  site: 'Site',
  suppression: 'Suppression',
}

export interface ActionFilters {
  type?: string
  state?: string
  nature?: string
  organizationId?: string
  siteId?: string
  /** The api's own enum, not a free string: every value the breakdown can show
   * has to be a value the filter accepts. */
  suppression?: 'shown' | 'suppressed'
}

/** Which filter each dimension writes to, so a breakdown row and a dropdown
 * are the same control reached two ways. */
export const DIMENSION_FILTER: Record<ActionDimension, keyof ActionFilters> = {
  type: 'type',
  state: 'state',
  nature: 'nature',
  organization: 'organizationId',
  site: 'siteId',
  suppression: 'suppression',
}

export interface DimensionCount {
  key: string
  label: string
  actions: number
}

export interface AdminActionRow {
  id: string
  type: string
  typeLabel: string
  nature: ActionNature
  state: ActionState
  source: string
  title: string
  headline: string | null
  subjectKey: string
  subjectKind: string
  organizationId: string
  organization: string
  siteId: string
  host: string
  valueClicks: number | null
  effort: string
  confidence: number | null
  priority: number
  /** The model's veto. Never shown to a customer, which is why it has to be
   * legible here. */
  suppressed: boolean
  skippedReason: string | null
  evidenceHash: string
  enrichedAt: string | null
  aiRunId: string | null
  detectedAt: string
  lastSeenAt: string
  closedAt: string | null
  closeReason: string | null
}

export interface ActionListData {
  filters: ActionFilters
  limit: number
  offset: number
  total: number
  actions: AdminActionRow[]
  /** Under the current filters. */
  breakdown: Record<ActionDimension, DimensionCount[]>
  /** Unfiltered: every key here is a value the filter accepts. */
  facets: Record<ActionDimension, DimensionCount[]>
}

export function useAdminActions(filters: ActionFilters, limit: number, offset: number) {
  return useQuery({
    queryKey: ['admin', 'actions', filters, limit, offset],
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // A 404 here is the staff gate, not a blip.
    retry: false,
    queryFn: async () => {
      const response = await api.api.admin.actions.$get({
        query: {
          limit: String(limit),
          offset: String(offset),
          ...(filters.type ? { type: filters.type } : {}),
          ...(filters.state ? { state: filters.state } : {}),
          ...(filters.nature ? { nature: filters.nature } : {}),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.siteId ? { siteId: filters.siteId } : {}),
          ...(filters.suppression ? { suppression: filters.suppression } : {}),
        },
      })
      return unwrap<ActionListData>(response)
    },
  })
}

export interface DetectorSiteRow {
  siteId: string
  host: string
  organizationId: string
  organization: string
  /** `ok`, `skipped` or `failed`. Null is the third state: this check has never
   * run against this site at all, which is not the same as finding nothing. */
  status: string | null
  startedAt: string | null
  finishedAt: string | null
  candidates: number
  opened: number
  refreshed: number
  closed: number
  error: string | null
  ageHours: number | null
}

export interface DetectorRow {
  type: string
  label: string
  nature: ActionNature
  requires: string[]
  lastRunAt: string | null
  stalestHours: number | null
  sites: DetectorSiteRow[]
  neverRun: number
  ok: number
  skipped: number
  failed: number
  candidates: number
  opened: number
  refreshed: number
  closed: number
  actions: number
  enriched: number
  suppressed: number
  /** Suppressed over enriched, null when nothing has been enriched — a row the
   * model has not looked at yet has not been approved by it. */
  suppressionRate: number | null
}

export function useDetectors() {
  return useQuery({
    queryKey: ['admin', 'detectors'],
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
    queryFn: async () =>
      unwrap<{ detectors: DetectorRow[]; now: string }>(
        await api.api.admin.actions.detectors.$get(),
      ),
  })
}

export interface EfficacyRow {
  type: string
  label: string
  nature: ActionNature
  metric: string
  improves: 'up' | 'down'
  measureAfterDays: number
  opened: number
  states: Record<ActionState, number>
  verified: number
  completed: number
  measured: number
  verdicts: Record<ActionVerdict, number>
  medianDelta: number | null
  medianControlDelta: number | null
  /** The median of the paired `delta - controlDelta`. The number that decides
   * whether a check earns its place: two separate medians can both be positive
   * while no single action beat its own control. */
  medianExcess: number | null
}

export function useEfficacy() {
  return useQuery({
    queryKey: ['admin', 'efficacy'],
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
    queryFn: async () =>
      unwrap<{ types: EfficacyRow[] }>(await api.api.admin.actions.efficacy.$get()),
  })
}

export interface ActionSubjectRow {
  id: number
  role: string
  kind: string
  queryId: number | null
  pageId: number | null
  referringDomainId: number | null
  value: string | null
  label: string | null
}

export interface ActionEventRow {
  id: number
  at: string
  kind: string
  userId: string | null
  userName: string | null
  message: string | null
  detail: Record<string, unknown> | null
}

export interface ActionOutcomeRow {
  id: number
  actionId: string
  measuredAt: string
  windowFrom: string
  windowTo: string
  metric: string
  baselineValue: number | null
  currentValue: number | null
  delta: number | null
  controlDelta: number | null
  verdict: ActionVerdict
  detail: Record<string, unknown> | null
}

export interface AdminActionDetail {
  action: AdminActionRow & {
    summary: string | null
    steps: string[] | null
    suggestion: string | null
    evidence: Record<string, unknown>
    baseline: Record<string, unknown>
    measureAfter: string | null
    parentActionId: string | null
    verifiedAt: string | null
    verifiedVia: string | null
    closedBy: string | null
    createdBy: string | null
    createdAt: string
    updatedAt: string
  }
  organization: { id: string; name: string; slug: string } | null
  site: { id: string; host: string; displayName: string } | null
  subjects: ActionSubjectRow[]
  events: ActionEventRow[]
  outcomes: ActionOutcomeRow[]
  run: AiRunDetail | null
}

export function useAdminAction(actionId: string) {
  return useQuery({
    queryKey: ['admin', 'action', actionId],
    staleTime: 15_000,
    retry: false,
    queryFn: async () => {
      const response = await api.api.admin.actions[':actionId'].$get({ param: { actionId } })
      return unwrap<AdminActionDetail>(response)
    },
  })
}

/** Describes an action again, past the evidence hash. The one spend on this
 * feed that is not a scan's. */
export function useReenrichAction(actionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await api.api.admin.actions[':actionId'].enrich.$post({
        param: { actionId },
      })
      return unwrap<{ queued: boolean; job: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'action', actionId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'ops'] })
    },
  })
}

/** The two names `/api/admin/sweep` accepts for this feed. Fixed here rather
 * than typed as a string, so a button that drifts from the allowlist is a
 * typecheck failure and not a 422 somebody finds by pressing it. */
export type ActionSweep = 'action-scan-all' | 'action-review-all'

/**
 * Every check, every site, now.
 *
 * Enqueues the fan-out rather than the work: the dispatcher is the one place
 * that knows which sites exist, so a sweep reaches a site added an hour ago
 * without anything here being told about it. Nothing comes back but the job —
 * how many sites it reaches is the dispatcher's answer and it has not run yet.
 */
export function useActionSweep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (sweep: ActionSweep) => {
      const response = await api.api.admin.sweep.$post({ json: { sweep } })
      return unwrap<{ queued: boolean; job: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ops'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'detectors'] })
    },
  })
}
